import type { SurveyQuestion } from '@/types/survey'

/** Match react-hook-form field keys for dotted question codes */
export function encodeSurveyFieldName(code: string): string {
  return code.replace(/\./g, '__DOT__')
}

export function isSurveyQuestionVisible(
  question: SurveyQuestion,
  getValue: (encodedFieldName: string) => unknown,
): boolean {
  if (!question.conditional_logic?.show_if) return true

  return question.conditional_logic.show_if.every((condition) => {
    const currentValue = getValue(encodeSurveyFieldName(condition.question_code))
    switch (condition.operator) {
      case 'equals':
        return currentValue === condition.value
      case 'not_equals':
        return currentValue !== condition.value
      case 'not_empty':
        return currentValue !== undefined && currentValue !== '' && currentValue !== null
      case 'contains':
        return Array.isArray(currentValue)
          ? currentValue.includes(condition.value)
          : String(currentValue).includes(condition.value)
      default:
        return true
    }
  })
}

/** Group consecutive questions with the same `category` (API-driven layout). */
export function buildSurveyCategorySections(
  sortedQuestions: SurveyQuestion[],
  isArabic: boolean,
): Array<{ key: string; label: string; questions: SurveyQuestion[] }> {
  const groups: Array<{ key: string; label: string; questions: SurveyQuestion[] }> = []
  let currentCategory = ''

  for (const question of sortedQuestions) {
    const category = question.category?.trim() || 'general'
    const label =
      category === 'general'
        ? isArabic
          ? 'عام'
          : 'General'
        : category.replace(/_/g, ' ')

    if (category !== currentCategory) {
      groups.push({ key: category, label, questions: [question] })
      currentCategory = category
    } else {
      groups[groups.length - 1].questions.push(question)
    }
  }

  return groups
}

export function surveyAnswersFilled(
  value: unknown,
  question: SurveyQuestion,
): boolean {
  if (value === undefined || value === null) return false
  if (question.type === 'multi_choice' || question.type === 'multi_file') {
    return Array.isArray(value) && value.length > 0
  }
  if (question.type === 'file') {
    const multi =
      question.multiple === true ||
      question.config?.multiple === true ||
      (typeof question.validation_rules?.max_files === 'number' &&
        (question.validation_rules.max_files as number) > 1)
    if (multi) return Array.isArray(value) && value.length > 0
    return typeof value === 'string' && value.length > 0
  }
  if (question.type === 'number') {
    if (value === '') return false
    if (typeof value === 'number') return !Number.isNaN(value)
    return String(value).trim().length > 0
  }
  if (question.type === 'boolean') return value === true
  if (question.type === 'yes_no' || question.type === 'yes_no_conditional') {
    if (value === undefined || value === null || value === '') return false
    return true
  }
  if (typeof value === 'string') return value.trim().length > 0
  if (typeof value === 'object') return true
  return false
}
